import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  IAddNodeSuggestion,
  IDeleteNodeSuggestion,
  IInventionTreeSuggestions,
  IUpdateNodeEdit,
  IUpdateNodeSuggestion,
} from 'features/Launchpad/types/inventionTree';
import { cloneDeep, uniqueId } from 'lodash';
import {
  FileInfo,
  InventionDisclosureTreeBranchI,
  PriorArtI,
  PriorArtItemI,
} from '../../api/inventionDisclosure/types';
import { PatentFigureI } from '../../api/patents/patentAttachment';
import { FilterSettings } from '../../types/types';
import { fetchAllPriorArt } from '../asyncThunk/AllPriorArtThunk';

export type AddedNode = InventionDisclosureTreeBranchI & { parentNodeId?: string };

interface SearchState {
  id: string;
  searchName: string;
  productDescription: string;
  pdf: { pdfId: string; pdfName: string }[];
  word: { wordId: string; wordName: string }[];
  images: { imageId: string; imageUrl: string }[];
  step: number;
  tree: InventionDisclosureTreeBranchI[];
  suggestions: IInventionTreeSuggestions;
  checkedSuggestion:
    | { type: 'add'; suggestion: IAddNodeSuggestion }
    | { type: 'delete'; suggestion: IDeleteNodeSuggestion }
    | { type: 'update'; suggestion: IUpdateNodeSuggestion }
    | null;
  priorArtTop: PriorArtItemI[];
  priorArtTopFigures: PatentFigureI[];
  priorArtAll: PriorArtItemI[];
  priorArtAllLoading: boolean;
  priorArtAllPageNum: number;
  priorArtTotal: number;
  claimsMode: boolean;
  branchToEdit: string[];
  editedNodes: IUpdateNodeEdit[];
  deletedNodes: string[];
  addedNodes: AddedNode[];
  groupByFamily: boolean;
  countryCodes: string[];
  statuses: string[];
  priorityFromDate: string;
  priorityToDate: string;
  patentId: string;
  selectedComponent: number;
  selectedPatentCardFields: string[];
  isBoostPerformance: boolean;
  fileInfo: FileInfo[];
  savedFilters: FilterSettings;
}

const initialState: SearchState = {
  id: '',
  searchName: '',
  productDescription: '',
  pdf: [],
  word: [],
  images: [],
  step: 0,
  suggestions: { addNodeSuggestions: [], updateNodeSuggestions: [], deleteNodeSuggestions: [] },
  checkedSuggestion: null,
  priorArtTop: [],
  priorArtTopFigures: [],
  priorArtAll: [],
  priorArtAllLoading: false,
  priorArtAllPageNum: 0,
  priorArtTotal: 0,
  claimsMode: false,
  branchToEdit: [],
  editedNodes: [],
  deletedNodes: [],
  addedNodes: [],
  tree: [],
  groupByFamily: true,
  countryCodes: [],
  statuses: [],
  priorityFromDate: '',
  priorityToDate: '',
  patentId: '',
  selectedComponent: 2,
  selectedPatentCardFields: [],
  isBoostPerformance: false,
  fileInfo: [],
  savedFilters: {
    groupByFamily: true,
    termFilters: [],
    dateRangeFilters: [],
  },
};

const inventionDisclosureSlice = createSlice({
  name: 'inventionDisclosure',
  initialState,
  reducers: {
    setInventionDisclosureName: (state, action: PayloadAction<string>) => {
      state.searchName = action.payload;
    },
    setInventionDisclosureId: (state, action: PayloadAction<string>) => {
      state.id = action.payload;
    },
    setInventionDisclosureDescription: (state, action: PayloadAction<string>) => {
      state.productDescription = action.payload;
    },
    setInventionDisclosurePDF: (state, action: PayloadAction<{ pdfId: string; pdfName: string }>) => {
      if (!state.pdf.find(file => file.pdfId === action.payload.pdfId)) {
        state.pdf = [...state.pdf, action.payload];
      }
    },
    removeInventionDisclosurePDF: (state, action: PayloadAction<string>) => {
      state.pdf = state.pdf.filter(file => file.pdfId !== action.payload);
    },
    setInventionDisclosureWordFile: (state, action: PayloadAction<{ wordId: string; wordName: string }>) => {
      if (!state.word.find(file => file.wordId === action.payload.wordId)) {
        state.word = [...state.word, action.payload];
      }
    },
    removeInventionDisclosureWordFile: (state, action: PayloadAction<string>) => {
      state.word = state.word.filter(file => file.wordId !== action.payload);
    },
    setInventionDisclosureImage: (state, action: PayloadAction<{ imageId: string; imageUrl: string }>) => {
      if (!state.images.find(image => image.imageId === action.payload.imageId)) {
        state.images = [...state.images, action.payload];
      }
    },
    removeInventionDisclosureImages: (state, action: PayloadAction<string>) => {
      state.images = state.images.filter(img => img.imageId !== action.payload);
    },
    removeInventionDisclosureFiles: (state, action: PayloadAction<string>) => {
      state.fileInfo = state.fileInfo.filter(file => file.fileId !== action.payload);
    },
    setInventionDisclosureTree: (state, action: PayloadAction<InventionDisclosureTreeBranchI[]>) => {
      state.tree = action.payload;
    },
    updateInventionDisclosureTreeNode: (state, action: PayloadAction<InventionDisclosureTreeBranchI>) => {
      state.tree = state.tree.map(branch => (branch.nodeId === action.payload.nodeId ? action.payload : branch));
    },
    setInventionDisclosureStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    setInventionDisclosureSelectedComponent: (state, action: PayloadAction<number>) => {
      state.selectedComponent = action.payload;
    },
    setSuggestions: (state, action: PayloadAction<IInventionTreeSuggestions>) => {
      state.suggestions = action.payload;
    },
    setCheckedSuggestion: (
      state,
      action: PayloadAction<
        | { type: 'add'; suggestion: IAddNodeSuggestion }
        | { type: 'delete'; suggestion: IDeleteNodeSuggestion }
        | { type: 'update'; suggestion: IUpdateNodeSuggestion }
        | null
      >,
    ) => {
      state.checkedSuggestion = action.payload;
    },
    setPriorArtTop: (state, action: PayloadAction<PriorArtItemI[]>) => {
      state.priorArtTop = action.payload;
    },
    setPriorArtTopFigures: (state, action: PayloadAction<PatentFigureI[]>) => {
      state.priorArtTopFigures = action.payload;
    },
    setFileInfo: (state, action: PayloadAction<FileInfo[]>) => {
      state.fileInfo = action.payload;
    },
    setPriorArtAll: (state, action: PayloadAction<PriorArtItemI[]>) => {
      state.priorArtAll = action.payload;
    },
    setPriorArtAllPageNum: (state, action: PayloadAction<number>) => {
      state.priorArtAllPageNum = action.payload;
    },
    setPriorArtTotal: (state, action: PayloadAction<number>) => {
      state.priorArtTotal = action.payload;
    },
    setClaimsMode: (state, action: PayloadAction<boolean>) => {
      state.claimsMode = action.payload;
    },
    setEditedNodes: (state, action: PayloadAction<{ node: IUpdateNodeEdit; type: 'draft' | 'original' }>) => {
      const { node, type } = action.payload;
      const nodeIndex = state.editedNodes.findIndex(existingNode => node.treeNodeId === existingNode.treeNodeId);

      if (type === 'original') {
        if (nodeIndex === -1) {
          state.editedNodes = [...state.editedNodes, node];
        } else {
          const copy = cloneDeep(state.editedNodes);
          copy.splice(nodeIndex, 1, node);
          state.editedNodes = [...copy];
        }
      } else {
        const updateAdded = (branches: InventionDisclosureTreeBranchI[]): InventionDisclosureTreeBranchI[] => {
          return branches.map(branch => {
            if (branch.nodeId === node.treeNodeId) {
              return { ...branch, title: node.title, description: node.description };
            }
            if (branch.children) {
              return { ...branch, children: updateAdded(branch.children) };
            }
            return branch;
          });
        };

        state.addedNodes = updateAdded([...state.addedNodes]);
      }
    },
    setDeletedNodes: (state, action: PayloadAction<{ node: string; type: 'draft' | 'original' }>) => {
      const { node, type } = action.payload;

      state.editedNodes = cloneDeep(state.editedNodes).filter(edited => edited.treeNodeId !== node);

      if (type === 'original') {
        state.deletedNodes = [...state.deletedNodes, node];
      } else {
        const deleteNode = (branches: InventionDisclosureTreeBranchI[]): InventionDisclosureTreeBranchI[] => {
          return branches
            .filter(branch => branch.nodeId !== node)
            .map(branch => {
              if (branch.children) {
                return { ...branch, children: deleteNode(branch.children) };
              }
              return branch;
            });
        };

        state.addedNodes = deleteNode([...state.addedNodes]);
      }
    },
    setAddedNodes: (
      state,
      action: PayloadAction<{
        parentNodeId: string;
        nodeType: InventionDisclosureTreeBranchI['nodeType'];
      }>,
    ) => {
      const { parentNodeId, nodeType } = action.payload;
      let doesExist = false;

      const addNode = (branches: InventionDisclosureTreeBranchI[]): InventionDisclosureTreeBranchI[] => {
        if (doesExist) {
          return branches;
        }
        const newBranches: InventionDisclosureTreeBranchI[] = [];
        for (let i = 0; i < branches.length; i++) {
          const branch = branches[i];
          if (branch.nodeId === parentNodeId) {
            doesExist = true;
            newBranches.push({
              ...branch,
              children: [
                {
                  title: '',
                  description: '',
                  nodeId: uniqueId(),
                  nodeType,
                  children: [],
                  orderIndex: branch.orderIndex + 1,
                  assessment: null,
                },
                ...branch.children,
              ],
            });
          } else {
            if (doesExist) {
              newBranches.push(branch);
            } else {
              newBranches.push({ ...branch, children: addNode(branch.children) });
            }
          }
        }
        return newBranches;
      };

      const updatedNodes = addNode([...state.addedNodes]);

      if (doesExist) {
        state.addedNodes = updatedNodes;
      } else {
        state.addedNodes = [
          {
            parentNodeId,
            title: '',
            description: '',
            nodeId: uniqueId(),
            nodeType,
            children: [],
            orderIndex: 0,
            assessment: null,
          },
          ...state.addedNodes,
        ];
      }
    },
    resetEdit: state => {
      state.editedNodes = [];
      state.deletedNodes = [];
      state.addedNodes = [];
    },
    setInventionDisclosureGroupByFamily: (state, action: PayloadAction<boolean>) => {
      state.groupByFamily = action.payload;
    },
    setBoostPerformance: (state, action: PayloadAction<boolean>) => {
      state.isBoostPerformance = action.payload;
    },
    setInventionDisclosureCountryCodes: (state, action: PayloadAction<string[]>) => {
      state.countryCodes = action.payload;
    },
    setSelectedPatentCardFields: (state, action: PayloadAction<string[]>) => {
      state.selectedPatentCardFields = action.payload;
    },
    setInventionDisclosureStatuses: (state, action: PayloadAction<string[]>) => {
      state.statuses = action.payload;
    },
    setInventionDisclosurePriorityFromDate: (state, action: PayloadAction<string>) => {
      state.priorityFromDate = action.payload;
    },
    setInventionDisclosurePriorityToDate: (state, action: PayloadAction<string>) => {
      state.priorityToDate = action.payload;
    },
    setPriorArtPatentId: (state, action: PayloadAction<string>) => {
      if (state.patentId === action.payload) {
        state.patentId = '';
      } else {
        state.patentId = action.payload;
      }
    },
    setSavedFilters(state, action: PayloadAction<FilterSettings>) {
      state.savedFilters = action.payload;
    },
    resetIDSlice: () => ({ ...initialState }),
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAllPriorArt.pending, state => {
        state.priorArtAllLoading = true;
      })
      .addCase(fetchAllPriorArt.fulfilled, (state, action: PayloadAction<PriorArtI>) => {
        state.priorArtAll = action.payload.priorArt;
        state.priorArtTop = action.payload.priorArt.slice(0, 20);
        state.priorArtTotal = action.payload.totalHits;
        state.priorArtAllLoading = false;
      })
      .addCase(fetchAllPriorArt.rejected, state => {
        state.priorArtAllLoading = false;
      });
  },
});

export default inventionDisclosureSlice.reducer;

// Action creators are generated for each case reducer function
export const {
  removeInventionDisclosurePDF,

  removeInventionDisclosureWordFile,

  setSuggestions,
  setCheckedSuggestion,

  setEditedNodes,
  setAddedNodes,
  resetIDSlice,
  resetEdit,

  setInventionDisclosureGroupByFamily,
  setInventionDisclosureCountryCodes,
  setInventionDisclosureStatuses,
  setInventionDisclosurePriorityFromDate,
  setInventionDisclosurePriorityToDate,

  setDeletedNodes,

  setSavedFilters,
} = inventionDisclosureSlice.actions;
